<template>
  <div ref="root" class="navigation-wrapper font-base">
    <button
      class="flex items-center gap-3"
      :aria-expanded="isMenuOpen"
      :aria-label="menuLabel"
      @click="toggleMenu"
    >
      <!-- Animated SVG Hamburger/X Icon (markenneutral; Beschriftung als Text daneben) -->
      <svg class="h-8" xmlns="http://www.w3.org/2000/svg" version="1.1" viewBox="0 0 91 56.7" aria-hidden="true">
        <!-- Animated Hamburger/X Lines -->
        <!-- Top line -->
        <rect
          class="menu-icon3 hamburger-svg-line"
          :x="isMenuOpen ? '15' : '6'"
          :y="isMenuOpen ? '23' : '2.8'"
          :width="isMenuOpen ? '60' : '79.4'"
          :height="isMenuOpen ? '10' : '11.3'"
          :transform="isMenuOpen ? 'rotate(45 45 28.35)' : ''"
        />
        <!-- Middle line -->
        <rect
          class="menu-icon4 hamburger-svg-line"
          x="6"
          y="22.7"
          width="79.4"
          height="11.3"
          :style="isMenuOpen ? 'opacity: 0;' : 'opacity: 1;'"
        />
        <!-- Bottom line -->
        <rect
          class="menu-icon1 hamburger-svg-line"
          :x="isMenuOpen ? '15' : '6'"
          :y="isMenuOpen ? '23' : '42.5'"
          :width="isMenuOpen ? '60' : '79.4'"
          :height="isMenuOpen ? '10' : '11.3'"
          :transform="isMenuOpen ? 'rotate(-45 45 28.35)' : ''"
        />
      </svg>
      <span class="hidden md:inline text-2xl uppercase tracking-wide text-default">{{ menuLabel }}</span>
    </button>

    <!-- Desktop Menu Overlay -->
    <div
      v-if="isMenuOpen"
      class="menu-wrapper mainnav text-lg 2xl:text-xl  font-light fixed left-0 right-0 bottom-0 z-50 hidden xl:block"
      :style="{ top: headerHeight + 'px' }"
    >
      <nav class="relative h-full">
        <!-- Navigation boxes — Spaltenzahl folgt der Anzahl der Top-Level-Items (max. 4) -->
        <div
          class="container grid gap-2 items-start justify-center text-accent-2"
          :class="gridColsClass"
        >
          <!-- Dynamic columns based on menu items -->
          <div
            v-for="item in menuItems"
            :key="item.title"
            class="bg-white shadow-xl py-6 px-4 overflow-y-auto"
            :style="{ maxHeight: 'calc(100vh - ' + (headerHeight + 24) + 'px)' }"
          >
            <a
              v-if="item.url && item.url !== '#'"
              :href="item.url"
              class="font-semibold font-base uppercase pb-2 block"
              :class="{ 'text-black': item.isActive }"
              :aria-current="item.isActive ? 'page' : null"
              @click="handleNavClick"
            >
              <span v-if="item.isActive">\</span> {{ item.title }}
            </a>
            <span v-else>{{ item.title }}</span>

            <!-- Sub navigation items -->
            <ul v-if="item.children && item.children.length > 0" class="">
              <li
                v-for="child in item.children"
                :key="child.title"
                class="border-t last-of-type:border-b border-accent-2"
              >
                <!-- Items with sub-children -->
                <div v-if="child.children && child.children.length > 0">
                  <div class="flex items-center justify-between w-full">
                    <a
                      v-if="child.url && child.url !== '#'"
                      :href="child.url"
                      class="uppercase block"
                      :class="{
                        'text-black': child.isActive,
                      }"
                    >
                      <span><span v-if="child.isActive">\</span> {{ child.title }}</span>
                    </a>
                    <button
                      class="border-l border-accent-2 px-2"
                      :aria-expanded="child.submenuOpen ? 'true' : 'false'"
                      :aria-label="(child.submenuOpen ? 'Untermenü schließen: ' : 'Untermenü öffnen: ') + child.title"
                      @click="toggleSubmenu(child)"
                    >
                      {{ child.submenuOpen ? "−" : "+" }}
                    </button>
                  </div>
                  <ul
                    v-if="child.submenuOpen"
                    class="text-lg"
                  >
                    <li
                      v-for="subChild in child.children"
                      :key="subChild.title"
                      class="border-t border-accent-2"
                    >
                      <a
                        :href="subChild.url"
                        class="ml-4 block"
                        :class="{
                          'text-black': subChild.isActive,
                        }"
                        :aria-current="subChild.isActive ? 'page' : null"
                        @click="handleNavClick"
                      >
                        <span v-if="subChild.isActive">\</span> {{ subChild.title }}
                      </a>
                    </li>
                  </ul>
                </div>

                <!-- Regular items -->
                <a
                  v-else
                  :href="child.url"
                  class="uppercase block"
                  :class="{ 'text-black': child.isActive }"
                  :aria-current="child.isActive ? 'page' : null"
                  @click="handleNavClick"
                >
                  <span v-if="child.isActive">\</span> {{ child.title }}
                </a>
              </li>
            </ul>
          </div>
        </div>
      </nav>
    </div>

    <!-- Mobile Menu Overlay -->
    <div v-if="isMenuOpen" class="fixed inset-x-0 bottom-0 z-50 bg-white xl:hidden" :style="{ top: headerHeight + 'px' }">
      <!-- Mobile Menu Content -->
      <div class="h-full text-accent-2 overflow-y-auto pb-20">
        <div class="">
          <div
            v-for="(item, index) in mobileMenuItems"
            :key="item.title"
            class="border-t border-accent-2"
            :class="{ 'border-b': index === mobileMenuItems.length - 1 }"
          >
            <!-- Top level items with children -->
            <div v-if="item.items && item.items.length > 0" class="flex items-center justify-between px-4">
              <a
                v-if="item.href && item.href !== '#'"
                :href="item.href"
                class="text-lg tracking-wide uppercase flex-1"
                :class="{ 'text-black': item.isActive }"
                :aria-current="item.isActive ? 'page' : null"
                @click="handleNavClick"
              >
                <span v-if="item.isActive">\</span> {{ item.title }}

              </a>
              <span v-else class="text-lg tracking-wide uppercase flex-1" :class="{ 'text-black': item.isActive }">
                <span v-if="item.isActive">\</span> {{ item.title }}
              </span>
              <button
                class="text-2xl pl-4 border-l border-accent-2"
                :aria-expanded="item.isOpen ? 'true' : 'false'"
                :aria-label="(item.isOpen ? 'Untermenü schließen: ' : 'Untermenü öffnen: ') + item.title"
                @click="toggleMobileSubmenu(item)"
              >
                {{ item.isOpen ? "−" : "+" }}
              </button>
            </div>
            <!-- Top level items without children -->
            <a
              v-else
              :href="item.href"
              class="block text-lg tracking-wide uppercase px-4 py-1"
              :class="{ 'text-black': item.isActive }"
              @click="handleNavClick"
            >
              <span v-if="item.isActive">\</span> {{ item.title }}
            </a>

            <div v-if="item.isOpen && item.items && item.items.length > 0" class="">
              <ul class="">
                <li v-for="subItem in item.items" :key="subItem.title" class="border-t border-accent-2" >
                  <!-- Sub items without children -->
                  <a
                    v-if="!subItem.children || subItem.children.length === 0"
                    :href="subItem.href"
                    class="block py-2 pl-8"
                    :class="{
                      'text-black': subItem.isActive,
                    }"
                    @click="handleNavClick"
                  >
                    <span v-if="subItem.isActive">\</span> {{ subItem.title }}
                  </a>
                  <!-- Sub items with children -->
                  <div v-else>
                    <div class="flex items-center justify-between pl-8">
                      <a
                        v-if="subItem.href && subItem.href !== '#'"
                        :href="subItem.href"
                        class="flex-1"
                        :class="{
                          'text-black': subItem.isActive,
                        }"
                        @click="handleNavClick"
                      >
                        <span v-if="subItem.isActive">\</span> {{ subItem.title }}
                      </a>
                      <span v-else class="text-gray-600 uppercase flex-1" :class="{ 'text-black': subItem.isActive }">
                        <span v-if="subItem.isActive">\</span> {{ subItem.title }}
                      </span>
                      <button
                        class="text-2xl px-4 border-l border-accent-2"
                        :aria-expanded="subItem.isOpen ? 'true' : 'false'"
                        :aria-label="(subItem.isOpen ? 'Untermenü schließen: ' : 'Untermenü öffnen: ') + subItem.title"
                        @click="toggleMobileSubmenu(subItem)"
                      >
                        {{ subItem.isOpen ? "−" : "+" }}
                      </button>
                    </div>
                    <ul v-if="subItem.isOpen" class="">
                      <li v-for="child in subItem.children" :key="child.title" class="border-t border-accent-2">
                        <a
                          :href="child.href"
                          class="block py-1 pl-12"
                          :class="{
                            'text-black': child.isActive,
                          }"
                          @click="handleNavClick"
                        >
                          <span v-if="child.isActive">\</span> {{ child.title }}
                        </a>
                      </li>
                    </ul>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from "vue";

const props = defineProps({
  navigationItems: {
    type: String,
    default: null,
  },
  // Logo-URL pro Site (via data-logo aus Header.fusion); kein hartkodierter Pfad
  logo: {
    type: String,
    default: null,
  },
  // Beschriftung des Menü-Buttons (via data-menu-label, übersetzbar) — die
  // frühere fest eingebaute „MENÜ"-SVG-Wortmarke war ein Brand-/Sprach-Leak.
  menuLabel: {
    type: String,
    default: "Menü",
  },
});

const root = ref(null);
const isMenuOpen = ref(false);
const menuItems = ref([]);
const mobileMenuItems = ref([]);
const headerHeight = ref(0);

// Volle Klassennamen (kein String-Bau) — sonst purged Tailwind sie aus dem Bundle
const gridColsClass = computed(
  () =>
    ({ 1: "grid-cols-1", 2: "grid-cols-2", 3: "grid-cols-3" })[
      menuItems.value.length
    ] || "grid-cols-4"
);

let resizeHandler = null;
let keydownHandler = null;

function processMobileMenuItems() {
  // Convert menu items to mobile format with isOpen state
  mobileMenuItems.value = menuItems.value.map((item) => ({
    title: item.title,
    href: item.url,
    isActive: item.isActive,
    isOpen:
      item.isActive ||
      (item.children &&
        item.children.some(
          (child) =>
            child.isActive ||
            (child.children &&
              child.children.some((subChild) => subChild.isActive))
        )),
    items: item.children
      ? item.children.map((child) => ({
          title: child.title,
          href: child.url,
          isActive: child.isActive,
          isOpen:
            child.isActive ||
            (child.children &&
              child.children.some((subChild) => subChild.isActive)),
          children: child.children
            ? child.children.map((subChild) => ({
                title: subChild.title,
                href: subChild.url,
                isActive: subChild.isActive,
              }))
            : [],
        }))
      : [],
  }));

  // Initialize submenu open states and check for active children
  menuItems.value.forEach((item) => {
    if (item.children && item.children.length > 0) {
      item.children.forEach((child) => {
        if (child.children && child.children.length > 0) {
          // Open submenu if any child is active or if this item is active
          const hasActiveChild = child.children.some(
            (subChild) => subChild.isActive
          );
          child.submenuOpen = child.isActive || hasActiveChild;
        }
      });
    }
  });
}

function setActiveStatesBasedOnUrl() {
  const currentPath = window.location.pathname;

  // Iterate through all menu items
  menuItems.value.forEach((item) => {
    // Check if this item is active
    item.isActive = currentPath === item.url;
    item.hasActiveChild = false;

    if (item.children && item.children.length > 0) {
      item.children.forEach((child) => {
        // Check if child is active
        child.isActive = currentPath === child.url;
        child.hasActiveChild = false;

        if (child.children && child.children.length > 0) {
          child.children.forEach((subChild) => {
            // Check if subchild is active
            subChild.isActive = currentPath === subChild.url;

            // If subchild is active, mark parent and grandparent as having active children
            if (subChild.isActive) {
              child.hasActiveChild = true;
              item.hasActiveChild = true;
            }
          });
        }

        // If child is active, mark parent as having active child
        if (child.isActive) {
          item.hasActiveChild = true;
        }
      });
    }
  });
}

function toggleMenu() {
  isMenuOpen.value = !isMenuOpen.value;
  if (isMenuOpen.value) {
    measureHeader();
  }
  updateBodyScroll();
}

function measureHeader() {
  const header = root.value && root.value.closest("header");
  headerHeight.value = header ? header.offsetHeight : 0;
}

function closeMenu() {
  isMenuOpen.value = false;
  updateBodyScroll();
}

// Toggles arbeiten direkt auf dem (reaktiven) Item-Objekt — Matching über den
// Titel würde bei doppelten Titeln mehrere/falsche Einträge treffen.
function toggleSubmenu(child) {
  child.submenuOpen = !child.submenuOpen;
}

function toggleMobileSubmenu(item) {
  item.isOpen = !item.isOpen;
}

function handleNavClick() {
  // Close menu after navigation
  closeMenu();
}

function updateBodyScroll() {
  if (isMenuOpen.value) {
    document.body.style.overflow = "hidden";
  } else {
    document.body.style.overflow = "";
  }
}

// equivalent to created(): parse the navigation data passed in from Neos
if (props.navigationItems) {
  try {
    const items = JSON.parse(props.navigationItems);

    // Check if menuItems is an array or needs conversion
    if (
      items.menuItems &&
      typeof items.menuItems === "object" &&
      !Array.isArray(items.menuItems)
    ) {
      menuItems.value = [];
    } else {
      menuItems.value = items.menuItems || [];
    }

    setActiveStatesBasedOnUrl();
    processMobileMenuItems();
  } catch (e) {
    console.error("[Navigation] Failed to parse navigation items:", e);
    console.error("[Navigation] Navigation data:", props.navigationItems);
  }
} else {
  console.warn("[Navigation] No navigation data provided from Neos");
}

onMounted(() => {
  measureHeader();
  resizeHandler = () => measureHeader();
  window.addEventListener("resize", resizeHandler);

  // Escape schließt das Menü; Tab bleibt im offenen Overlay gefangen
  // (Focus-Trap — sonst wandert der Fokus in die dahinterliegende Seite).
  keydownHandler = (e) => {
    if (!isMenuOpen.value) return;
    if (e.key === "Escape") {
      closeMenu();
      return;
    }
    if (e.key === "Tab" && root.value) {
      const focusables = root.value.querySelectorAll(
        'a[href], button:not([disabled])'
      );
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  };
  document.addEventListener("keydown", keydownHandler);
});

onUnmounted(() => {
  document.body.style.overflow = "";
  if (resizeHandler) {
    window.removeEventListener("resize", resizeHandler);
  }
  if (keydownHandler) {
    document.removeEventListener("keydown", keydownHandler);
  }
});
</script>
